import { BLOG_IMAGE_VARIANTS } from "@/generated/blog-image-variants";

/**
 * Responsive sources for editorial raster images (blog covers, inline article
 * figures, project thumbnails). The originals are full-size PNG/JPG exports of
 * up to 1.5 MB; serving them as the LCP element kept desktop LCP above 4s.
 *
 * - Supabase Storage objects go through the Storage image transformation
 *   endpoint, which resizes and negotiates WebP from the browser's Accept
 *   header. Same bucket, same object, so the visual is unchanged.
 * - Local files under /assets/blog use the WebP variants generated by
 *   scripts/optimize-blog-images.mjs and listed in the manifest.
 * - Anything else (SVG, third-party hosts, unknown files) is returned as is.
 */
export type ResponsiveImage = {
  src: string;
  srcSet?: string;
  width?: number;
  height?: number;
};

const SUPABASE_OBJECT =
  /^(https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1)\/object\/public\/([^?#]+\.(?:png|jpe?g|webp))$/i;
const SITE_ORIGIN = /^https:\/\/(?:www\.)?shugert\.com\.mx(?=\/)/i;
const DEFAULT_WIDTHS = [640, 960, 1280, 1600];

function supabaseRender(base: string, objectPath: string, width: number): string {
  return `${base}/render/image/public/${objectPath}?width=${width}&quality=75`;
}

export function responsiveImage(
  src: string | null | undefined,
  widths: number[] = DEFAULT_WIDTHS,
): ResponsiveImage {
  if (!src) return { src: "" };

  const supabase = SUPABASE_OBJECT.exec(src);
  if (supabase) {
    const [, base, objectPath] = supabase;
    const sorted = [...widths].sort((a, b) => a - b);
    const fallback = sorted.find((w) => w >= 1280) ?? sorted[sorted.length - 1];
    return {
      src: supabaseRender(base, objectPath, fallback),
      srcSet: sorted.map((w) => `${supabaseRender(base, objectPath, w)} ${w}w`).join(", "),
    };
  }

  const localPath = src.replace(SITE_ORIGIN, "");
  const local = BLOG_IMAGE_VARIANTS[localPath];
  if (local && local.variants.length) {
    return {
      src: local.variants[local.variants.length - 1].src,
      srcSet: local.variants.map((v) => `${v.src} ${v.width}w`).join(", "),
      width: local.width,
      height: local.height,
    };
  }

  return { src };
}
